import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ColumnVisibilityModels } from '../../Components/Table/ColumnVisibilityModels';
import { UseColumnVisibilityProps } from '../../types';
import { getUserKey } from '../../utils/filtersStorage';

const STORAGE_KEY_PREFIX = 'columnVisibility';
const ORDER_STORAGE_KEY_PREFIX = 'columnOrder';

export function useColumnVisibility({
  isMapping,
  mappingContent,
  restrictChemical,
  view,
  tab,
  user,
  isEvidence,
}: UseColumnVisibilityProps) {
  // Unique keys
  const storageKey = useMemo(() => {
    const parts: string[] = [];

    if (view) parts.push(view);
    if (tab) parts.push(tab);
    if (isMapping && mappingContent?.columVisibilityModelKey) {
      parts.push(mappingContent.columVisibilityModelKey);
    }
    if (isEvidence) parts.push('evidence');
    if (restrictChemical) parts.push('restricted');

    const suffix = parts.length > 0 ? `_${parts.join('_')}` : '';

    try {
      const userKey = user ? getUserKey(user) : null;
      if (userKey) {
        return `${STORAGE_KEY_PREFIX}:${userKey}${suffix}`;
      }
    } catch (err) {
      // ignore and fall back to global key
    }

    return `${STORAGE_KEY_PREFIX}${suffix}`;
  }, [view, tab, isMapping, mappingContent?.columVisibilityModelKey, isEvidence, restrictChemical, user]);

  const orderStorageKey = useMemo(() => {
    return storageKey.replace(STORAGE_KEY_PREFIX, ORDER_STORAGE_KEY_PREFIX);
  }, [storageKey]);

  const selectColumnVisibilityModel = useMemo(() => {
    const fnColumnVisibilityModel =
      isMapping && mappingContent?.isActive
        ? () => ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, isEvidence)
        : () => ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const fnColumnVisibilityModelWithRestrictions = () => {
      const originalList = fnColumnVisibilityModel();
      const restrictedList = restrictChemical
        ? ['gof_lof', 'chemsInSentence', 'chemID', 'chemIDs', 'chemical_effect']
        : [];
      return originalList && restrictedList
        ? Object.fromEntries(Object.entries(originalList).filter(([key]) => !restrictedList.includes(key)))
        : undefined;
    };

    return fnColumnVisibilityModelWithRestrictions;
  }, [view, tab, mappingContent?.columVisibilityModelKey, restrictChemical, isMapping, isEvidence]);

  const baseModelRef = useRef(selectColumnVisibilityModel());

  const [columnVisibilityModel, setColumnVisibilityModelState] = useState(() => {
    const baseModel = selectColumnVisibilityModel();

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const userChanges = JSON.parse(saved);
        return { ...baseModel, ...userChanges };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      localStorage.removeItem(storageKey);
    }

    return baseModel;
  });

  // Order statement
  const [columnOrder, setColumnOrderState] = useState<string[] | undefined>(() => {
    try {
      const saved = localStorage.getItem(orderStorageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column order from localStorage:', error);
      localStorage.removeItem(orderStorageKey);
    }
    return undefined;
  });

  const setColumnVisibilityModel = useCallback(
    (newValueOrUpdater: any) => {
      setColumnVisibilityModelState(prevState => {
        const newValue =
          typeof newValueOrUpdater === 'function' ? newValueOrUpdater(prevState) : newValueOrUpdater;

        try {
          // Only save differences from base model
          const baseModel = baseModelRef.current;
          const userChanges: Record<string, boolean> = {};

          Object.keys(newValue || {}).forEach(key => {
            if (newValue[key] !== baseModel?.[key]) {
              userChanges[key] = newValue[key];
            }
          });

          if (Object.keys(userChanges).length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(userChanges));
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }

        return newValue;
      });
    },
    [storageKey, selectColumnVisibilityModel],
  );

  // Columns order mangement
  const setColumnOrder = useCallback(
    (newOrder: string[]) => {
      setColumnOrderState(newOrder);

      try {
        localStorage.setItem(orderStorageKey, JSON.stringify(newOrder));
      } catch (error) {
        console.error('Error saving column order to localStorage:', error);
      }
    },
    [orderStorageKey],
  );

  useEffect(() => {
    const newBaseModel = selectColumnVisibilityModel();
    baseModelRef.current = newBaseModel;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const userChanges = JSON.parse(saved);
        setColumnVisibilityModelState({ ...newBaseModel, ...userChanges });
      } else {
        setColumnVisibilityModelState(newBaseModel);
      }
    } catch (error) {
      setColumnVisibilityModelState(newBaseModel);
    }

    const savedOrder = localStorage.getItem(orderStorageKey);
    if (savedOrder) {
      try {
        setColumnOrderState(JSON.parse(savedOrder));
      } catch (error) {
        setColumnOrderState(undefined);
      }
    } else {
      setColumnOrderState(undefined);
    }
  }, [selectColumnVisibilityModel, storageKey, orderStorageKey]);

  // Sync columnOrder when new visible columns appear
  useEffect(() => {
    if (!columnOrder || !columnVisibilityModel) return;

    const visibleKeys = Object.keys(columnVisibilityModel).filter(key => columnVisibilityModel[key] === true);
    const missing = visibleKeys.filter(key => !columnOrder.includes(key));

    if (missing.length > 0) {
      const newOrder = [...columnOrder, ...missing];
      setColumnOrder(newOrder);

      try {
        localStorage.setItem(orderStorageKey, JSON.stringify(newOrder));
      } catch (error) {
        console.error('Error updating column order in localStorage:', error);
      }
    }
  }, [columnVisibilityModel]);

  return { columnVisibilityModel, setColumnVisibilityModel, columnOrder, setColumnOrder };
}
